/*
 * Data sync hook — mantiene la app al día con la base de datos.
 *
 * Estrategia:
 *  - Poll ligero cada 45s a /api/sync/version (solo counts + last_modified).
 *  - Compara con el snapshot anterior; si algo cambió, emite un evento
 *    `sf:data-changed` con el detail `{ collections: [...], reason: 'poll' }`.
 *  - Refresca inmediatamente al recuperar la conexión (`online`) y al volver
 *    a poner la pestaña en primer plano (`visibilitychange`).
 *  - Además avisa al service worker para que limpie el caché de /api/*.
 *
 * Las páginas se suscriben con `useSyncListener(reload, ['sites', 'incidents'])`
 * y así refrescan solo cuando cambia lo que consumen.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../api/client';

const POLL_INTERVAL_MS = 45000;
const STORAGE_KEY = 'sf_sync_snapshot';

// Envía mensaje al SW para invalidar caché de API.
const askServiceWorkerToClearApiCache = () => {
  try {
    if (navigator?.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
    }
  } catch { /* ignore */ }
};

const diffSnapshots = (prev, next) => {
  if (!prev) return [];
  const changed = [];
  for (const [key, meta] of Object.entries(next || {})) {
    const before = prev[key];
    if (!before) { changed.push(key); continue; }
    if (before.count !== meta.count || before.last_modified !== meta.last_modified) {
      changed.push(key);
    }
  }
  return changed;
};

const useDataSync = ({ enabled = true, intervalMs = POLL_INTERVAL_MS } = {}) => {
  const [status, setStatus] = useState({ lastSyncAt: null, syncing: false, changedAt: null });
  const snapshotRef = useRef(null);
  const inFlightRef = useRef(false);

  // Carga snapshot previo del storage para que la primera comparación tenga sentido.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        snapshotRef.current = parsed?.collections || null;
        if (parsed?.lastSyncAt) setStatus((s) => ({ ...s, lastSyncAt: parsed.lastSyncAt }));
      }
    } catch { /* ignore */ }
  }, []);

  const check = useCallback(async (reason = 'poll') => {
    if (!enabled || inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus((s) => ({ ...s, syncing: true }));
    try {
      const { data } = await api.get('/sync/version');
      const next = data?.collections || {};
      const prev = snapshotRef.current;
      const changed = diffSnapshots(prev, next);
      const now = new Date().toISOString();

      snapshotRef.current = next;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ collections: next, lastSyncAt: now }));
      } catch { /* ignore */ }

      // Solo emitir si había baseline y hubo cambios reales.
      if (prev && changed.length > 0) {
        askServiceWorkerToClearApiCache();
        window.dispatchEvent(new CustomEvent('sf:data-changed', { detail: { collections: changed, reason } }));
        setStatus({ lastSyncAt: now, syncing: false, changedAt: now });
      } else {
        setStatus((s) => ({ lastSyncAt: now, syncing: false, changedAt: s.changedAt }));
      }
    } catch {
      setStatus((s) => ({ ...s, syncing: false }));
    } finally {
      inFlightRef.current = false;
    }
  }, [enabled]);

  // Poll + eventos de visibilidad/online.
  useEffect(() => {
    if (!enabled) return;
    check('initial');
    const t = setInterval(() => check('poll'), intervalMs);
    const onVis = () => { if (document.visibilityState === 'visible') check('visibility'); };
    const onOnline = () => check('online');
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    // Refetch manual desde cualquier parte de la app.
    const onManual = () => check('manual');
    window.addEventListener('sf:sync-now', onManual);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('sf:sync-now', onManual);
    };
  }, [enabled, intervalMs, check]);

  return { status, syncNow: () => check('manual') };
};

// Helper de suscripción para las páginas.
// `collections` es opcional; si se pasa, solo dispara `onChange` cuando alguna
// de esas colecciones aparece en el evento (evita renders innecesarios).
export const useSyncListener = (onChange, collections) => {
  useEffect(() => {
    const handler = (e) => {
      const changed = e.detail?.collections || [];
      if (!collections || collections.some((c) => changed.includes(c))) {
        onChange(e.detail);
      }
    };
    window.addEventListener('sf:data-changed', handler);
    return () => window.removeEventListener('sf:data-changed', handler);
  }, [onChange, collections]);
};

export default useDataSync;
